/**
 * Glitch text reveal.
 * Every character of [data-glitch-text] appears in random order,
 * flickering through a few random kana before settling on the real one.
 */
(() => {
  // Glyph pools live in the page markup ([data-glitch-pool]) so the Adobe Fonts
  // dynamic subset covers them. Kana only, to keep the noise soft.
  // "wide" (full-width kana) stands in for Japanese, "narrow" (half-width kana) for
  // Latin letters, so a scrambling glyph roughly keeps the real character's width.
  const FALLBACK_POOL = Array.from('ｱｲｳｴｵｶｷｸｹｺ');
  const readPool = (name) => {
    const el = document.querySelector(`[data-glitch-pool="${name}"]`);
    const chars = el ? Array.from(el.textContent).filter((c) => !/\s/.test(c)) : [];
    return chars.length ? chars : FALLBACK_POOL;
  };
  let WIDE_POOL = FALLBACK_POOL;
  let NARROW_POOL = FALLBACK_POOL;

  // Each character starts at a random moment within its block's window (random order).
  const HEADING_WINDOW = 700; // ms
  const TEXT_WINDOW = 1200;
  const SCRAMBLE_MIN = 260; // how long a character keeps flickering
  const SCRAMBLE_MAX = 620;
  const FLICKER_MIN = 45; // how long each wrong glyph stays
  const FLICKER_MAX = 90;
  const BLOCK_DELAY = 300; // the next block starts this long after the previous one starts

  const random = (min, max) => min + Math.random() * (max - min);
  const pick = (pool) => pool[Math.floor(Math.random() * pool.length)];
  const poolFor = (char) => (char.codePointAt(0) < 0x2e80 ? NARROW_POOL : WIDE_POOL);

  function splitIntoChars(root) {
    const spans = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((node) => {
      const fragment = document.createDocumentFragment();
      Array.from(node.textContent).forEach((char) => {
        if (/\s/.test(char)) {
          fragment.appendChild(document.createTextNode(char));
          return;
        }
        const span = document.createElement('span');
        span.className = 'glitch-char is-waiting';
        span.textContent = char;
        fragment.appendChild(span);
        spans.push(span);
      });
      node.parentNode.replaceChild(fragment, node);
    });

    return spans;
  }

  function init(scope = document) {
    const blocks = Array.from(scope.querySelectorAll('[data-glitch-text]:not(.is-glitch-ready)'));
    if (!blocks.length) return;

    WIDE_POOL = readPool('wide');
    NARROW_POOL = readPool('narrow');

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const chars = [];

    // PC and SP copies animate on their own timelines; only one of them is visible.
    const offsets = { pc: 0, sp: 0 };

    blocks.forEach((block) => {
      const spans = splitIntoChars(block);
      block.classList.add('is-glitch-ready');

      if (reduceMotion) {
        spans.forEach((span) => span.classList.remove('is-waiting'));
        return;
      }

      const track = block.classList.contains('coming-soon__sp') ? 'sp' : 'pc';
      const revealWindow = block.tagName === 'H1' ? HEADING_WINDOW : TEXT_WINDOW;

      spans.forEach((span) => {
        const start = offsets[track] + random(0, revealWindow);
        const end = start + random(SCRAMBLE_MIN, SCRAMBLE_MAX);
        const pool = poolFor(span.textContent);
        chars.push({ span, start, end, pool, nextFlip: start, state: 'waiting' });
      });

      offsets[track] += BLOCK_DELAY;
    });

    if (!chars.length) return;

    let origin = null;
    const tick = (now) => {
      if (origin === null) origin = now;
      const elapsed = now - origin;
      let pending = 0;

      chars.forEach((c) => {
        if (c.state === 'done') return;
        pending++;
        if (elapsed < c.start) return;

        if (elapsed >= c.end) {
          c.span.classList.remove('is-waiting', 'is-scrambling');
          c.span.removeAttribute('data-glyph');
          c.state = 'done';
          return;
        }

        if (c.state === 'waiting') {
          c.span.classList.remove('is-waiting');
          c.span.classList.add('is-scrambling');
          c.state = 'scrambling';
        }

        if (elapsed >= c.nextFlip) {
          c.span.setAttribute('data-glyph', pick(c.pool));
          c.nextFlip = elapsed + random(FLICKER_MIN, FLICKER_MAX);
        }
      });

      if (pending) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  // Start once the Adobe Fonts kit has finished (wf-loading removed), so the
  // reveal isn't interrupted by a font swap. The kit itself times out after 3s.
  const whenFontsSettled = (callback) => {
    const root = document.documentElement;
    if (!root.classList.contains('wf-loading')) return callback();
    const observer = new MutationObserver(() => {
      if (root.classList.contains('wf-loading')) return;
      observer.disconnect();
      callback();
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
  };

  const start = () => whenFontsSettled(() => init());
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Re-run when the section is edited in the theme editor.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
